import { A11yModule } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  Output,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { RichTextEditorComponent } from 'shared/feature-rich-text-editor';
import { RichTextViewComponent } from 'shared/feature-rich-text-view';
import {
  ChipComponent,
  IconDirective,
  MenuComponent,
  MenuItemDirective,
  MenuTriggerDirective,
  PopoverService,
  TooltipDirective,
} from 'shared/ui-common';

import { GuidelineTag, GuidelineTagStatus } from '@treetop/shared/model';

import { Position } from '../../../../../../../shared/util-common/src/lib/position.util';

@Component({
  selector: 'tt-guideline-update-tag',
  templateUrl: './guideline-update-tag.component.html',
  styleUrls: ['./guideline-update-tag.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChipComponent,
    TranslocoPipe,
    TooltipDirective,
    MenuComponent,
    MenuItemDirective,
    IconDirective,
    MatIconModule,
    MenuTriggerDirective,
    RichTextEditorComponent,
    A11yModule,
    RichTextViewComponent,
  ],
  host: {
    '[attr.data-testid]': '`guideline-update-tag--${tag().isActive ? "active" : "inactive"}`',
  },
})
export class GuidelineUpdateTagComponent {
  private popoverService = inject(PopoverService);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef = inject(ElementRef);

  @Input() public readonly = false;

  public tag = input.required<GuidelineTag>();

  @Output() public tagUpdated = new EventEmitter<GuidelineTag>();

  @HostListener('click', ['$event'])
  public onClick($event: MouseEvent) {
    if (this.readonly) {
      return;
    }

    $event.stopImmediatePropagation();
  }

  protected addMessagePopover = viewChild.required<TemplateRef<unknown>>('addMessagePopover');
  protected viewMessagePopover = viewChild.required<TemplateRef<unknown>>('viewMessagePopover');

  protected tagLabel = computed(() => {
    const tag = this.tag();

    const guidelineUpdateTagLabelKeyPrefix = 'GuidelineUpdateTag';

    switch (tag.status) {
      case GuidelineTagStatus.CHILD_UPDATED:
        return `${guidelineUpdateTagLabelKeyPrefix}.ChildUpdated`;
      case GuidelineTagStatus.NEW:
        return `${guidelineUpdateTagLabelKeyPrefix}.New`;
      case GuidelineTagStatus.UPDATED:
        return `${guidelineUpdateTagLabelKeyPrefix}.Updated`;
      case GuidelineTagStatus.DELETED:
        return `${guidelineUpdateTagLabelKeyPrefix}.Deleted`;
      default:
        return '';
    }
  });

  protected tagVariant = computed(() => {
    if (this.tag()?.status === GuidelineTagStatus.DELETED) {
      return 'ready';
    }

    if (!this.tag()?.isActive) {
      return 'ghostDisabled';
    }

    if (this.tag()?.status === GuidelineTagStatus.NEW) {
      return 'published';
    }

    return 'approved';
  });

  protected handleTagUpdate(tag: Partial<GuidelineTag>) {
    this.tagUpdated.emit({
      ...this.tag(),
      ...tag,
    });
  }

  protected handleAddMessage() {
    this.popoverService
      .open(
        this.addMessagePopover(),
        this.elementRef.nativeElement as HTMLElement,
        {
          data: {
            tag: this.tag(),
          },
          positionStrategy: 'bottom-right',
          offsetY: 5,
        },
        this.viewContainerRef,
      )
      .afterClosed()
      .subscribe((message) => {
        if (message) {
          this.handleTagUpdate({ message });
        }
      });
  }

  protected onViewMessage($event: MouseEvent) {
    if (!this.tag()?.message) {
      return;
    }

    this.popoverService.open(
      this.viewMessagePopover(),
      this.elementRef.nativeElement as HTMLElement,
      {
        data: {
          tag: this.tag(),
        },
        positionStrategy: 'bottom-right',
        offsetY: 5,
      },
      this.viewContainerRef,
    );

    $event.stopPropagation();
  }

  protected readonly Position = Position;
}
